import type { Applicability, FlowStatus, Goal, StatusStep, StepAddr } from "./advisoryApi";

// The step navigator's model, derived from GET /status?workflow=internal.
//
// /status lists required steps in QP's traversal order (page → goal →
// portfolio), which puts Portfolio Construction before Risk Assessment and
// Financial Situation. Andersen's advisors work in a fixed order instead
// (STEP_ORDER below), so each QP page is placed by what it contains
// (componentType / kind), falling back to its name. Consecutive steps of the
// same page (and goal / portfolio) stay one item — the unit QP's wizard renders
// as a tab. Pages we can't place keep their /status order, just before
// "Your advice".

export type NavStatus = "complete" | "open" | "not_applicable" | "optional";

export interface NavItem {
  id: string;
  kind: "page" | "wealth-plan" | "review";
  title: string;
  /** Goal / portfolio suffix when the flow has several. */
  subtitle: string | null;
  pageId?: string;
  goalId?: string;
  portfolioId?: string;
  steps: StatusStep[];
  status: NavStatus;
  /** Why a not-applicable step isn't open yet, in the advisor's words. */
  naHint?: string;
  /** Position in STEP_ORDER (UNPLACED for pages we can't place). */
  rank: number;
}

export interface NavGroup {
  key: string;
  title: string | null;
  items: NavItem[];
}

export const REVIEW_ID = "review";
export const WEALTH_PLAN_ID = "wealth-plan";

// The advisor's order. Custom portfolio shares Portfolio Construction's rank and
// follows it (same goal/portfolio) by /status order.
const RANK = {
  adviceInformation: 1,
  purpose: 2,
  risk: 3,
  financial: 4,
  knowledge: 5,
  sustainability: 6,
  portfolio: 7,
  analytics: 8,
  xray: 9,
  wealthPlan: 10,
  review: 11,
} as const;
const UNPLACED = 10.5; // after the wealth plan, before "Your advice"

const ANALYTICS_TYPES = new Set([
  "portfolio_display",
  "portfolio_forecast",
  "expected_value",
  "historical_return",
  "cost_chart",
  "asset_allocation",
]);

// Fallback by page name, for pages whose contents don't say where they belong.
const RANK_BY_NAME: [RegExp, number][] = [
  [/^advice information/i, RANK.adviceInformation],
  [/^purpose/i, RANK.purpose],
  [/^risk/i, RANK.risk],
  [/^financial/i, RANK.financial],
  [/^knowledge/i, RANK.knowledge],
  [/^sustainab/i, RANK.sustainability],
  [/^(portfolio construction|custom portfolio)/i, RANK.portfolio],
  [/^analytics/i, RANK.analytics],
  [/x-?ray/i, RANK.xray],
];

/** Where a page goes in the advisor's order. The most specific content wins. */
function rankOf(title: string, steps: StatusStep[]): number {
  const types = new Set(steps.map((s) => s.componentType ?? s.kind));
  if (types.has("portfolio_xray")) return RANK.xray;
  if ([...types].some((t) => ANALYTICS_TYPES.has(t))) return RANK.analytics;
  if (types.has("portfolio_input") || types.has("custom_portfolio")) return RANK.portfolio;
  if (types.has("sustainability")) return RANK.sustainability;
  // The portfolio's sustainability match sits with the results (QP's Analytics page).
  if (types.has("sustainability_status")) return RANK.analytics;
  if (types.has("product_assessment")) return RANK.knowledge;
  if (types.has("financial_situation")) return RANK.financial;
  if (types.has("risk_assessment")) return RANK.risk;
  if (types.has("goal_input")) return RANK.purpose;
  if (steps.some((s) => s.kind === "field")) return RANK.adviceInformation;
  return RANK_BY_NAME.find(([re]) => re.test(title))?.[1] ?? UNPLACED;
}

function rollup(steps: StatusStep[]): NavStatus {
  if (steps.some((s) => s.status === "open")) return "open";
  if (steps.length > 0 && steps.every((s) => s.status === "not_applicable")) return "not_applicable";
  return "complete";
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

/** The advisor-facing reason a not-applicable step isn't open yet. */
function naHintFor(rank: number, applicability: Applicability | null): string {
  switch (rank) {
    case RANK.knowledge: {
      const types = applicability?.knowledge.productTypes ?? [];
      return types.length
        ? `Not applicable yet — opens when the portfolio contains ${listNames(types)}`
        : "Not applicable yet — opens when a custom portfolio holds products that need a knowledge check";
    }
    case RANK.analytics:
      return "Not applicable yet — appears once the goal, risk assessment and portfolio are in place";
    case RANK.xray:
      return "Not applicable yet — appears once the portfolio resolves to funds";
    case RANK.portfolio:
      return "Not applicable yet — opens when the portfolio type is Custom";
    default:
      return "Not applicable yet";
  }
}

export function itemAddr(item: NavItem): StepAddr {
  return { pageId: item.pageId, goalId: item.goalId, portfolioId: item.portfolioId };
}

export function buildNav(
  status: FlowStatus | null,
  goals: Goal[],
  applicability: Applicability | null = null,
): { groups: NavGroup[]; flat: NavItem[] } {
  const steps = status?.steps ?? [];
  const items: NavItem[] = [];
  const byKey = new Map<string, NavItem>();
  const goalIndex = new Map(goals.map((g, i) => [g.id, { goal: g, index: i }]));
  const multiGoal = goals.length > 1;

  for (const s of steps) {
    if (!s.pageId) continue; // instance-level requirements belong to the review
    const key = `${s.pageId}:${s.goalId ?? ""}:${s.portfolioId ?? ""}`;
    let item = byKey.get(key);
    if (!item) {
      item = {
        id: `page:${key}`,
        kind: "page",
        title: s.pageName ?? "Step",
        subtitle: subtitleFor(s, goalIndex, multiGoal),
        pageId: s.pageId,
        goalId: s.goalId,
        portfolioId: s.portfolioId,
        steps: [],
        status: "open",
        rank: UNPLACED,
      };
      byKey.set(key, item);
      items.push(item);
    }
    item.steps.push(s);
  }

  const knowledgeVacuous = applicability !== null && !applicability.knowledge.applicable;
  for (const it of items) {
    it.rank = rankOf(it.title, it.steps);
    it.status = rollup(it.steps);
    // QP reports K&E `complete` while no holding brings a product type into
    // use — vacuous. Show it in place as not applicable yet, never as done.
    if (it.rank === RANK.knowledge && knowledgeVacuous && it.status === "complete") it.status = "not_applicable";
    if (it.status === "not_applicable") it.naHint = naHintFor(it.rank, applicability);
  }

  // Stable sort: rank, then goal, then portfolio, then /status order.
  const order = new Map(items.map((it, i) => [it, i]));
  const goalPos = (it: NavItem) => (it.goalId ? (goalIndex.get(it.goalId)?.index ?? 99) : -1);
  const portfolioPos = (it: NavItem) => {
    if (!it.goalId || !it.portfolioId) return -1;
    const g = goalIndex.get(it.goalId)?.goal;
    const i = g?.portfolios.findIndex((p) => p.id === it.portfolioId) ?? -1;
    return i < 0 ? 99 : i;
  };
  const sorted = [...items].sort(
    (a, b) =>
      a.rank - b.rank || goalPos(a) - goalPos(b) || portfolioPos(a) - portfolioPos(b) || order.get(a)! - order.get(b)!,
  );
  const placed = sorted.filter((it) => it.rank < RANK.wealthPlan);
  const unplaced = sorted.filter((it) => it.rank >= RANK.wealthPlan);

  const instanceSteps = steps.filter((s) => !s.pageId);
  const wealthPlan: NavItem = {
    id: WEALTH_PLAN_ID,
    kind: "wealth-plan",
    title: "Wealth plan",
    subtitle: "Optional",
    steps: [],
    status: "optional",
    rank: RANK.wealthPlan,
  };
  const review: NavItem = {
    id: REVIEW_ID,
    kind: "review",
    title: "Your advice",
    subtitle: null,
    steps: instanceSteps,
    status: status?.canSubmit ? "complete" : "open",
    rank: RANK.review,
  };

  const flat = [...placed, wealthPlan, ...unplaced, review];
  const groups: NavGroup[] = [
    { key: "flow", title: null, items: placed },
    { key: "finish", title: null, items: [wealthPlan, ...unplaced, review] },
  ];
  return { groups, flat };
}

// With several goals the order interleaves them (Purpose for each goal, then
// Risk for each …), so each goal-scoped item names its goal; a portfolio item
// also names its portfolio when its goal has several.
function subtitleFor(
  s: StatusStep,
  goalIndex: Map<string, { goal: Goal; index: number }>,
  multiGoal: boolean,
): string | null {
  const parts: string[] = [];
  const g = s.goalId ? goalIndex.get(s.goalId) : undefined;
  if (multiGoal && s.goalId) parts.push(g ? g.goal.name || `Goal ${g.index + 1}` : "Goal");
  if (s.portfolioId && g && g.goal.portfolios.length > 1) {
    const pIdx = g.goal.portfolios.findIndex((p) => p.id === s.portfolioId);
    parts.push(`Portfolio ${pIdx + 1}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

/** First open page in the advisor's order, else the review — where an advisor should land. */
export function firstOpen(flat: NavItem[]): NavItem | undefined {
  return flat.find((i) => i.kind === "page" && i.status === "open") ?? flat.find((i) => i.kind === "review");
}

/** The neighbours of an item in the advisor's order (the Previous / Next pager). */
export function neighbours(flat: NavItem[], id: string | null): { prev: NavItem | null; next: NavItem | null } {
  const i = flat.findIndex((x) => x.id === id);
  if (i < 0) return { prev: null, next: null };
  return { prev: flat[i - 1] ?? null, next: flat[i + 1] ?? null };
}

/** The next OPEN step after this one in the advisor's order, if any — "continue where it's needed". */
export function nextOpen(flat: NavItem[], id: string | null): NavItem | null {
  const i = flat.findIndex((x) => x.id === id);
  return flat.slice(i + 1).find((x) => x.kind === "page" && x.status === "open") ?? null;
}
